/**
 * Sidenav — Main sidebar navigation for Kadmin
 *
 * Filters routes by activeRole and renders collapse items.
 * Reads the role from localStorage to match routeRole on each route definition.
 */

import { useEffect, useContext } from "react";
import { useLocation, NavLink } from "react-router-dom";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import SidenavCollapse from "components/Sidenav/SidenavCollapse";
import SidenavRoot from "components/Sidenav/SidenavRoot";

import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";
import { AuthContext } from "context/AuthContext";
import { useT } from "context/I18nContext";
import { useGlobalNotifications } from "context/NotificationContext";
import { usePortalContext } from "context/PortalContext";

function Sidenav({ color, brand, brandName, routes, onMouseEnter, onMouseLeave, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const { activeRole } = useContext(AuthContext);
  const t = useT();
  const { unreadCount } = useGlobalNotifications();
  const { activePortal, getNavigation } = usePortalContext();
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");
  const isInPortal = location.pathname.startsWith("/portal/");

  // whiteSidenav means "Light" sidenav - always use dark text
  let textColor = "white";
  if (transparentSidenav || whiteSidenav) {
    textColor = "dark";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  // Responsive: auto-collapse on small screens
  useEffect(() => {
    function handleResize() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, location]);

  // Filter routes: only show collapse items for the active role
  const filteredRoutes = routes.filter(
    (r) => r.routeRole === activeRole && r.type === "collapse"
  );

  // Portal navigation items (when user is in a portal)
  const portalNavItems = isInPortal && activePortal ? getNavigation() : [];

  const renderRoutes = filteredRoutes.map(({ name, nameKey, icon, key, route, module }) => (
    <NavLink key={key} to={route} style={{ textDecoration: "none" }}>
      <SidenavCollapse
        name={nameKey ? t(nameKey) : name}
        icon={icon}
        active={key === collapseName || location.pathname === route}
        badge={module === "notifications" ? unreadCount : 0}
      />
    </NavLink>
  ));

  const renderPortalNav = portalNavItems.map((navItem) => {
    const navRoute = navItem.route || `/portal/${activePortal.slug}/${navItem.pageId || navItem.id}`;
    const isActive = location.pathname === navRoute;
    const navIcon = navItem.icon ? <Icon>{navItem.icon}</Icon> : <Icon>article</Icon>;
    const navLabel = navItem.labelKey ? t(navItem.labelKey) : navItem.label;

    return (
      <NavLink key={navItem.id} to={navRoute} style={{ textDecoration: "none" }}>
        <SidenavCollapse
          name={navLabel}
          icon={navIcon}
          active={isActive}
        />
      </NavLink>
    );
  });

  // Determine brand name — use portal name when in a portal
  const displayBrandName = isInPortal && activePortal
    ? (activePortal.themeOverrides?.brandName || activePortal.name)
    : brandName;

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Brand / logo area */}
      <KDBox pt={3} pb={1} px={4} textAlign="center">
        <KDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <KDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </KDTypography>
        </KDBox>

        <KDBox component={NavLink} to="/" display="flex" alignItems="center" gap={1}>
          {brand && (
            <KDBox component="img" src={brand} alt="brand" width="2rem" />
          )}
          <KDBox
            sx={(theme) => ({
              opacity: miniSidenav ? 0 : 1,
              transition: theme.transitions.create("opacity", {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.standard,
              }),
            })}
          >
            <KDTypography component="span" variant="h6" fontWeight="bold" color={textColor}>
              {displayBrandName}
            </KDTypography>
          </KDBox>
        </KDBox>
      </KDBox>

      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />

      {/* Nav items */}
      <List sx={{ px: 1 }}>
        {isInPortal && portalNavItems.length > 0 ? renderPortalNav : renderRoutes}
      </List>

      {/* Portal: show link back to main app */}
      {isInPortal && (
        <List sx={{ px: 1, mt: "auto" }}>
          <NavLink to={`/${activeRole}/dashboard`} style={{ textDecoration: "none" }}>
            <SidenavCollapse
              name={t("nav.backToMain") || "Back to Main"}
              icon={<Icon>arrow_back</Icon>}
              active={false}
            />
          </NavLink>
        </List>
      )}
    </SidenavRoot>
  );
}

export default Sidenav;
